import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { DropdownAdminOption } from '@/types/adminDropdowns'

interface DropdownOptionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode: 'create' | 'edit'
  option?: DropdownAdminOption | null
  isPending: boolean
  onSubmit: (payload: {
    value: string
    display_label: string
    display_order?: number
    is_active?: boolean
  }) => void
  usageBanner?: React.ReactNode
}

export function DropdownOptionDialog({
  open,
  onOpenChange,
  mode,
  option,
  isPending,
  onSubmit,
  usageBanner,
}: DropdownOptionDialogProps) {
  const [value, setValue] = useState('')
  const [displayLabel, setDisplayLabel] = useState('')
  const [displayOrder, setDisplayOrder] = useState('')
  const [isActive, setIsActive] = useState(true)

  useEffect(() => {
    if (!open) {
      return
    }

    setValue(option?.value ?? '')
    setDisplayLabel(option?.display_label ?? '')
    setDisplayOrder(option ? String(option.display_order) : '')
    setIsActive(option?.is_active ?? true)
  }, [open, option])

  const handleSubmit = () => {
    const trimmedValue = value.trim()
    const trimmedLabel = displayLabel.trim()

    if (!trimmedValue || !trimmedLabel) {
      return
    }

    const parsedOrder = displayOrder.trim() ? Number(displayOrder) : undefined

    onSubmit({
      value: trimmedValue,
      display_label: trimmedLabel,
      display_order:
        typeof parsedOrder === 'number' && !Number.isNaN(parsedOrder)
          ? parsedOrder
          : undefined,
      ...(mode === 'edit' ? { is_active: isActive } : {}),
    })
  }

  const title =
    mode === 'create' ? 'Add Dropdown Option' : 'Edit Dropdown Option'
  const submitLabel = mode === 'create' ? 'Add Option' : 'Save Changes'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-130">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Configure the option value, label, and display order.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="dropdown-value">Value</Label>
            <Input
              id="dropdown-value"
              placeholder="e.g. DIRECT"
              value={value}
              onChange={e => setValue(e.target.value)}
              disabled={isPending}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="dropdown-label">Display Label</Label>
            <Input
              id="dropdown-label"
              placeholder="e.g. Direct"
              value={displayLabel}
              onChange={e => setDisplayLabel(e.target.value)}
              disabled={isPending}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="dropdown-order">Display Order (optional)</Label>
            <Input
              id="dropdown-order"
              type="number"
              min={1}
              value={displayOrder}
              onChange={e => setDisplayOrder(e.target.value)}
              disabled={isPending}
            />
          </div>

          {mode === 'edit' && usageBanner}

          {mode === 'edit' ? (
            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <p className="text-sm font-medium text-foreground">
                  Active status
                </p>
                <p className="text-xs text-muted-foreground">
                  Inactive options are hidden from dropdowns by default.
                </p>
              </div>
              <Button
                type="button"
                variant={isActive ? 'secondary' : 'outline'}
                onClick={() => setIsActive(prev => !prev)}
                disabled={isPending}
              >
                {isActive ? 'Active' : 'Inactive'}
              </Button>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={
              isPending ||
              value.trim().length === 0 ||
              displayLabel.trim().length === 0
            }
          >
            {isPending ? 'Saving...' : submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
